import React from 'react';
import { Alert } from 'reactstrap';

import { formatDexCopyPreview } from '~dex/lib/dexCopyPreview';

import ConfirmDialog from '~components/ConfirmDialog';
import { DEX_FIELD_TAG_COLUMNS } from '~appointments/components/dexFields';

import { DexDetailsBody } from '~appointments/actions/dexDetails';

interface ComponentProps {
  isOpen: boolean;
  /** The source appointment's reference number, falling back to its raw id if it has none. */
  sourceReference: string | number | null;
  /** The DEX values about to be saved, as they would be written. */
  values: Partial<DexDetailsBody> | null;
  /** The field tags the DEX form actually rendered. Everything outside them is hidden. */
  fieldTags?: string[];
  onClose: (confirm: boolean) => void;
}

/**
 * DexCopyConfirmDialog
 *
 * Confirms, before the appointment is saved, that its DEX session data was copied from another
 * appointment and names which one.
 *
 * DEX session data is reported to DSS. Copying it from another appointment without the person
 * saving ever seeing it means an incorrect figure can be submitted on their behalf, from a record
 * they never looked at - so the copy is stated explicitly and has to be acknowledged.
 *
 * Blends the two precedents the ticket cites, which do not agree on their own: the interaction
 * model is ConfirmDialog's blocking Yes/No modal (already used twice on this page), while the body
 * uses the same `color="warning"` framing as the DEX export screen's data-quality warning. The
 * field/value list reuses formatDexCopyPreview - the same formatter behind the readiness report's
 * copy popover - so "what is about to be written" is presented identically wherever it appears.
 */
const DexCopyConfirmDialog: React.FC<ComponentProps> = ({ isOpen, sourceReference, values, fieldTags, onClose }) => {
  // toApiBody returns a complete row, defaulting every column this DEX type does not use. Listing
  // those reads as noise on a dialog whose whole purpose is asking someone to check the values, so
  // the list is masked to the fields the form actually showed.
  const columns = fieldTags?.length ? fieldTags.flatMap((tag) => DEX_FIELD_TAG_COLUMNS[tag] ?? []) : null;
  const masked = columns
    ? Object.fromEntries(Object.entries(values ?? {}).filter(([column]) => columns.includes(column)))
    : (values ?? null);
  const entries = formatDexCopyPreview(masked as Record<string, string | number | boolean | null> | null);

  const content = (
    <div data-cy="dex-copy-confirm-body">
      <Alert color="warning" className="mb-3">
        <p className="mb-2">
          The DEX session data below was copied from appointment <strong>#{sourceReference ?? 'unknown'}</strong>.
        </p>
        <p className="mb-0">
          You are about to copy across information that, under government data collection rules, must be correct
          per-session and per-client. Please review and approve items copied before continuing.
        </p>
      </Alert>
      {entries.length > 0 && (
        <dl className="row mb-0" data-cy="dex-copy-confirm-values">
          {entries.map(({ column, label, value }) => (
            <React.Fragment key={column}>
              <dt className="col-6 fw-normal text-muted">{label}</dt>
              <dd className="col-6 mb-1">{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );

  return <ConfirmDialog isOpen={isOpen} messages={content} onClose={onClose} useList={false} />;
};

export default DexCopyConfirmDialog;
